import React, { useState } from 'react';
import { XCircleIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import ResolutionOptionsDrawer from '../steps/validation/ResolutionOptionsDrawer';

const ResolutionsSection = ({
    validationData,
    resolutionsHook,
    getResolutionData,
    onSendReminder,
    sentReminders,
    getUserInfo,
}) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    // Compute unresolved counts separated by role (rower vs coach)
    const { rowerUnresolvedCount, coachUnresolvedCount, totalUnresolved } = (() => {
        const availabilityCheck = validationData?.availability_check || {};
        const missingRowers = availabilityCheck.missing_users || [];
        const missingCoaches = availabilityCheck.missing_coaches || [];

        let ignoredRowers = 0;
        let ignoredCoaches = 0;

        const isCoach = (uid) => {
            const id = parseInt(uid);
            return (
                (availabilityCheck.missing_coaches || []).some((c) => c.user_id === id) ||
                (availabilityCheck.coaches_with_availability || []).some((c) => c.user_id === id)
            );
        };

        Object.entries(resolutionsHook.perDayResolutions || {}).forEach(([userId, userResolutions]) => {
            const hasIgnored = Object.values(userResolutions || {}).some((resolution) => resolution === 'ignore');
            if (hasIgnored) {
                if (isCoach(userId)) ignoredCoaches += 1; else ignoredRowers += 1;
            }
        });

        const rowerUnresolvedCount = (missingRowers?.length || 0) + ignoredRowers;
        const coachUnresolvedCount = (missingCoaches?.length || 0) + ignoredCoaches;
        return {
            rowerUnresolvedCount,
            coachUnresolvedCount,
            totalUnresolved: rowerUnresolvedCount + coachUnresolvedCount,
        };
    })();

    return (
        <div className="rounded-lg bg-white p-4 shadow sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex-1">
                    <div className="mb-2 flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-sm font-medium text-blue-600">
                            1
                        </div>
                        <div>
                            <h3 className="text-base font-medium text-gray-900">Fix Missing Availability</h3>
                            <p className="text-sm text-gray-600">Resolve missing availability data</p>
                        </div>
                    </div>

                    {totalUnresolved > 0 ? (
                        <div className="ml-11">
                            {rowerUnresolvedCount > 0 && (
                                <div className="mb-2 flex items-center gap-2">
                                    <XCircleIcon className="h-4 w-4 text-red-500" />
                                    <span className="text-sm text-red-600">
                                        {rowerUnresolvedCount} rower{rowerUnresolvedCount === 1 ? '' : 's'} missing availability
                                    </span>
                                </div>
                            )}
                            {coachUnresolvedCount > 0 && (
                                <div className="mb-2 flex items-center gap-2">
                                    <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />
                                    <span className="text-sm text-yellow-700">
                                        {coachUnresolvedCount} coach{coachUnresolvedCount === 1 ? '' : 'es'} missing availability (auto-ignored unless explicitly included)
                                    </span>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="ml-11">
                            <div className="flex items-center gap-2 text-green-600">
                                <CheckCircleIcon className="h-4 w-4 text-green-500" />
                                <span className="text-sm">All required availability provided</span>
                            </div>
                        </div>
                    )}
                </div>
                <div className="sm:ml-4">
                    <button
                        onClick={() => setIsDrawerOpen(true)}
                        className={`w-full rounded px-3 py-1.5 text-sm font-medium text-white sm:w-auto ${totalUnresolved > 0 ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-600 hover:bg-gray-700'
                            }`}
                    >
                        {totalUnresolved > 0 ? 'Fix Availability' : 'Edit Availability'}
                    </button>
                </div>
            </div>
            <ResolutionOptionsDrawer
                open={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                rowers={(() => {
                    const { rowers } = getResolutionData();
                    return rowers;
                })()}
                coaches={(() => {
                    const { coaches } = getResolutionData();
                    return coaches;
                })()}
                getCurrentResolution={resolutionsHook.getCurrent}
                hasAppliedResolution={resolutionsHook.hasApplied}
                onChangeResolution={(userId, action, date) => {
                    if (date === null) {
                        const { rowers, coaches } = getResolutionData();
                        const allUsers = [...rowers, ...coaches];
                        const user = allUsers.find((u) => u.user_id === userId);
                        if (user && user.dates.length > 0) {
                            user.dates.forEach((missingDate) => {
                                resolutionsHook.setResolution(userId, action, missingDate);
                            });
                        }
                    } else {
                        resolutionsHook.setResolution(userId, action, date);
                    }
                }}
                onSendReminder={onSendReminder}
                sentReminders={sentReminders}
            />
        </div>
    );
};

export default ResolutionsSection;


